import { useTranslation } from "react-i18next"

import type { AdminViewEntry } from "../admin-search-registry"

import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { cn } from "@/lib/utils"

import { AdminNavigationGroup } from "./admin-navigation-group"

type AdminUsageNavigationProps = {
  views: AdminViewEntry[]
  value: string
  onValueChange: (view: string) => void
}

const GROUPS = [
  { id: "activity", label: "Activity", ids: ["activity", "adoption", "training", "feedback"] },
  {
    id: "billing",
    label: "Costs & budgets",
    ids: [
      "costs",
      "fair-use-drift",
      "fair-use-users",
      "fair-use-leads",
      "fair-use-costs",
      "fair-use-plans",
      "fair-use-settings",
    ],
  },
] as const

export function AdminUsageNavigation({ views, value, onValueChange }: AdminUsageNavigationProps) {
  const { t } = useTranslation()
  const groups = GROUPS.map((group) => ({
    ...group,
    label: t(`adminDashboard.workspaceNavigation.usageGroups.${group.id}`, group.label),
    views: group.ids
      .map((id) => views.find((view) => view.id === id))
      .filter((view): view is AdminViewEntry => Boolean(view)),
  })).filter((group) => group.views.length > 0)

  return (
    <>
      <div className="xl:hidden">
        <label className="text-muted-foreground mb-1.5 block text-xs font-semibold tracking-wider uppercase">
          {t("adminDashboard.workspaceNavigation.usageMobileLabel", "Usage & billing view")}
        </label>
        <Select value={value} onValueChange={onValueChange}>
          <SelectTrigger
            className="min-h-11 w-full rounded-md"
            aria-label={t(
              "adminDashboard.workspaceNavigation.usageAriaLabel",
              "Usage & billing views"
            )}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {groups.map((group) => (
              <SelectGroup key={group.id}>
                <SelectLabel>{group.label}</SelectLabel>
                {group.views.map((view) => (
                  <SelectItem key={view.id} value={view.id}>
                    {t(view.labelKey, view.labelFallback)}
                  </SelectItem>
                ))}
              </SelectGroup>
            ))}
          </SelectContent>
        </Select>
      </div>
      <nav
        aria-label={t("adminDashboard.workspaceNavigation.usageAriaLabel", "Usage & billing views")}
        className="border-border bg-card hidden self-start rounded-lg border p-2 xl:sticky xl:top-4 xl:block"
      >
        {groups.map((group) => (
          <AdminNavigationGroup
            key={group.id}
            activeKey={group.views.some((view) => view.id === value) ? value : undefined}
            label={group.label}
            storageKey={`admin-usage-group-${group.id}`}
          >
            {group.views.map((view) => {
              const Icon = view.icon
              const selected = value === view.id
              return (
                <button
                  key={view.id}
                  aria-current={selected ? "page" : undefined}
                  type="button"
                  className={cn(
                    "text-foreground hover:bg-accent focus-visible:ring-ring flex min-h-11 w-full items-center gap-2 rounded-md border px-3 py-2 text-left text-sm focus-visible:ring-2 focus-visible:outline-none",
                    selected
                      ? "border-primary-foreground bg-primary text-primary-foreground font-semibold"
                      : "border-transparent"
                  )}
                  onClick={() => onValueChange(view.id)}
                >
                  <Icon
                    aria-hidden="true"
                    className={cn(
                      "size-4 shrink-0",
                      selected ? "text-primary-foreground" : "text-muted-foreground"
                    )}
                  />
                  <span>{t(view.labelKey, view.labelFallback)}</span>
                </button>
              )
            })}
          </AdminNavigationGroup>
        ))}
      </nav>
    </>
  )
}
